import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import Logo from "@/components/Logo";
import ReminderBell from "@/components/ReminderBell";
import VoiceSettings from "@/components/VoiceSettings";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { MessageSquare, BookOpen, Bookmark, GraduationCap, LogOut, User, Lightbulb, ClipboardList, BarChart3, ScrollText, Rocket, Briefcase, Users, Volume2, Compass } from "lucide-react";

const HR_ITEMS = [
  { to: "/app/chat", label: "Assistant", icon: MessageSquare, tid: "nav-chat" },
  { to: "/app/employee-center", label: "Employee Center", icon: Compass, tid: "nav-employee-center" },
  { to: "/app/action-center", label: "Manager Center", icon: Briefcase, tid: "nav-action-center", managerOnly: true },
  { to: "/app/documents", label: "Library", icon: BookOpen, tid: "nav-documents" },
  { to: "/app/practices", label: "Best Practices", icon: Lightbulb, tid: "nav-practices" },
  { to: "/app/forms", label: "Forms", icon: ClipboardList, tid: "nav-forms" },
  { to: "/app/dashboard", label: "KPIs", icon: BarChart3, tid: "nav-dashboard" },
  { to: "/app/onboarding", label: "Onboarding", icon: Rocket, tid: "nav-onboarding" },
  { to: "/app/highlights", label: "Highlights", icon: Bookmark, tid: "nav-highlights" },
  { to: "/app/audit", label: "Audit", icon: ScrollText, tid: "nav-audit", managerOnly: true },
  { to: "/app/team", label: "Team", icon: Users, tid: "nav-team", managerOnly: true },
];
const LUMIS_AI_ITEM = { to: "/app/readiness", label: "LUMIS AI™ Academy", icon: GraduationCap, tid: "nav-readiness" };

function NavItem({ it, compact }) {
  return (
    <NavLink
      to={it.to}
      data-testid={compact ? `${it.tid}-mobile` : it.tid}
      className={({ isActive }) =>
        compact
          ? `px-3 py-1.5 rounded-md text-xs whitespace-nowrap flex items-center gap-1.5 ${isActive ? "bg-[#FDECD0] text-[#7C3F00]" : "text-slate-600"}`
          : `px-2 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${isActive ? "bg-[#FDECD0] text-[#7C3F00]" : "text-slate-700 hover:bg-slate-100"}`
      }
    >
      <it.icon className={compact ? "w-3.5 h-3.5" : "w-4 h-4 hidden min-[2100px]:block"} /> {it.label}
    </NavLink>
  );
}

function LumisAiNav({ compact }) {
  return (
    <NavLink
      to={LUMIS_AI_ITEM.to}
      data-testid={compact ? "nav-readiness-mobile" : "nav-readiness"}
      title="LUMIS AI™ Academy — workforce AI readiness and AI in HR implementation resources"
      className={({ isActive }) =>
        `${compact ? "px-3 py-1.5 text-xs rounded-md" : "px-3 py-2 text-sm rounded-lg"} font-semibold flex items-center gap-2 whitespace-nowrap transition-colors border ${
          isActive ? "bg-[#0A2A5E] text-white border-[#0A2A5E]" : "bg-white text-[#0A2A5E] border-[#0A2A5E]/30 hover:border-[#0A2A5E]"
        }`
      }
    >
      <span className="w-2 h-2 rounded-full bg-[#F5A623]" />
      <GraduationCap className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} /> LUMIS AI™ Academy
    </NavLink>
  );
}

export default function AppShell({ children }) {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const items = HR_ITEMS.filter((it) => !it.managerOnly || user?.role === "manager");
  const [voiceOpen, setVoiceOpen] = useState(false);

  const toggleOptIn = async (v) => {
    try {
      const { data } = await api.patch("/auth/preferences", { reminder_opt_in: v });
      setUser(data);
      toast.success(v ? "Reminders on" : "Reminders off");
    } catch { toast.error("Could not update preference"); }
  };

  return (
    <div className="min-h-screen bg-[#F5F9FE]">
      <header className="no-print sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/app/chat" data-testid="brand-logo" className="flex items-center gap-2 flex-shrink-0">
            <Logo size={40} />
            <div className="leading-tight hidden sm:block">
              <div className="font-display font-extrabold text-[#0A2A5E] text-lg tracking-tight">LUMIS HR</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#C7761A] font-semibold">AI Assistant</div>
            </div>
          </Link>

          <nav data-testid="desktop-nav" className="hidden 2xl:flex items-center gap-0.5 min-w-0 flex-1 overflow-x-auto">
            {items.map((it) => <NavItem key={it.to} it={it} />)}
            <span className="mx-1.5 h-6 w-px bg-slate-300 flex-shrink-0" aria-hidden="true" />
            <LumisAiNav />
          </nav>

          <div className="flex items-center gap-2 flex-shrink-0">
          <ReminderBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="user-menu-trigger" variant="outline" className="border-slate-300">
                <User className="w-4 h-4 mr-2" /> {user?.name?.split(" ")[0] || "Account"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>
                <div className="text-xs text-slate-500">Signed in as</div>
                <div className="font-semibold truncate">{user?.email}</div>
                <div className="text-xs mt-1 inline-block px-2 py-0.5 rounded-full bg-[#FDECD0] text-[#7C3F00] font-semibold uppercase tracking-wider">
                  {user?.role === "manager" ? "HR Manager" : "Employee"}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {user?.role === "manager" && (
                <div className="px-2 py-2 flex items-center justify-between gap-2" onClick={(e) => e.stopPropagation()}>
                  <div>
                    <div className="text-sm font-medium">Compliance reminders</div>
                    <div className="text-[11px] text-slate-500">Receive deadline reminders</div>
                  </div>
                  <Switch data-testid="reminder-opt-in" checked={user?.reminder_opt_in !== false} onCheckedChange={toggleOptIn} />
                </div>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="voice-settings-btn" onClick={() => setVoiceOpen(true)}>
                <Volume2 className="w-4 h-4 mr-2" />
                <span className="flex-1">Reading voice</span>
                <span className="text-[11px] text-slate-500 capitalize">{user?.tts_voice || "nova"} · {Number(user?.tts_speed || 1).toFixed(2).replace(/0$/, "")}×</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}>
                <LogOut className="w-4 h-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <VoiceSettings open={voiceOpen} onOpenChange={setVoiceOpen} />
          </div>
        </div>
        <div data-testid="compact-nav" className="2xl:hidden border-t border-slate-200 px-2 py-2 flex overflow-x-auto gap-1 items-center">
          {items.map((it) => <NavItem key={it.to} it={it} compact />)}
          <span className="mx-1 h-5 w-px bg-slate-300 flex-shrink-0" aria-hidden="true" />
          <LumisAiNav compact />
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
}
